import type { ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { ForcePasswordChangePage } from "@/pages/core/ForcePasswordChangePage";

interface Props {
  children: ReactNode;
  permission?: string;
  /** Any-of gate: passes when the user holds AT LEAST ONE of the listed
   *  permissions (e.g. a route shared by editors and approvers). */
  anyPermission?: string[];
}

export function RequireAuth({ children, permission, anyPermission }: Props) {
  const { user, loading, hasPermission, hasAnyPermission } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-caption text-gray-mid uppercase tracking-wide">
        Loading Phoenix Capital…
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Admin-assigned password (invite or reset): block everything behind a
  // forced change until the user sets their own. The change-password
  // endpoint clears the flag; refresh() lifts the gate.
  //
  // …EXCEPT while impersonating. A freshly-invited user has this flag set, so
  // without the bypass an admin impersonating one would land on the forced
  // change-password screen with no way past it except actually changing that
  // user's real password — which impersonation explicitly forbids. The FLAG
  // itself stays honest (the target still owes their own password); only the
  // gate yields, and only for a session that is not the target's own. Do not
  // "tidy" this back to a bare `user.mustChangePassword`.
  if (user.mustChangePassword && !user.impersonation) {
    return <ForcePasswordChangePage />;
  }

  const denied =
    (permission && !hasPermission(permission)) ||
    (anyPermission && anyPermission.length > 0 && !hasAnyPermission(anyPermission));
  if (denied) {
    return (
      <div className="max-w-reading mx-auto p-space-5 text-center">
        <h2 className="font-display font-bold text-h2">Access denied</h2>
        <p className="text-body text-gray-mid mt-space-3">
          Your role does not have permission to view this area. Contact your administrator if
          you believe this is a mistake.
        </p>
      </div>
    );
  }

  return <>{children}</>;
}
